/* eslint-disable no-param-reassign */
/* eslint-disable no-console */
import React, { useState, useMemo } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { PresetMediaItem } from 'src/pages/MachineGroupDetails/UpsertPresetForm';
import { PortalRole, UserProfile } from 'src/backend';
import { useSelector } from 'react-redux';
import { getUserProfile } from 'src/components/UserStore/userProfileSlice';
import MediaTileComponent from './MediaTileComponent';
import { Customer } from '../Dashboard/CreateEditMachineGroupForm';

const DropZone = styled(Box)(({ theme }) => ({
  transition: 'all 0.2s ease-in-out',
  '&.drop-target': {
    backgroundColor: theme.palette.primary.light,
    border: `2px dashed ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
  },
}));

interface OtherMediaSectionProps {
  media: PresetMediaItem[];
  customers: Customer[];
  onDeleteMedia: (media: PresetMediaItem) => void;
  onShowPresets: (media: PresetMediaItem) => void;
  onMoveMedia: (
    mediaId: string,
    folderId: string | null,
    mediaName: string,
  ) => void;
}

function OtherMediaSection({
  media,
  customers,
  onDeleteMedia,
  onShowPresets,
  onMoveMedia,
}: OtherMediaSectionProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const userProfile = useSelector(getUserProfile) as UserProfile;

  // Helper function to extract customer ID from media URL
  const getCustomerIdFromUrl = (fullFileUrl: string): string | null => {
    const match = fullFileUrl.match(/\/media\/([a-f0-9-]{36})/i);
    return match ? match[1] : null;
  };

  // Group media by customer
  const groupedMedia = useMemo(() => {
    const mediaByCustomer: { [customerId: string]: PresetMediaItem[] } = {};
    const unknownCustomerMedia: PresetMediaItem[] = [];

    media.forEach((mediaItem) => {
      const customerId = getCustomerIdFromUrl(mediaItem.fullFileUrl);
      if (customerId) {
        if (!mediaByCustomer[customerId]) {
          mediaByCustomer[customerId] = [];
        }
        mediaByCustomer[customerId].push(mediaItem);
      } else {
        unknownCustomerMedia.push(mediaItem);
      }
    });

    return { mediaByCustomer, unknownCustomerMedia };
  }, [media]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'));
      const { mediaId, mediaName } = data;
      onMoveMedia(mediaId, null, mediaName);
    } catch (error) {
      console.error('Error parsing drag data:', error);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Other Media
      </Typography>
      <DropZone
        className={isDragOver ? 'drop-target' : ''}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          minHeight: 100,
        }}
      >
        {/* Render media grouped by customer */}
        {Object.entries(groupedMedia.mediaByCustomer).map(
          ([customerId, customerMedia]) => {
            const customer = customers.find((c) => c.id === customerId);
            const customerName = customer?.name || 'Unknown Customer';

            return (
              <Box key={customerId} sx={{ mb: 2 }}>
                {userProfile?.role === PortalRole[PortalRole.Admin] && (
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: 'primary.main',
                    }}
                  >
                    {customerName}
                  </Typography>
                )}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                  }}
                >
                  {customerMedia.map((mediaItem) => (
                    <MediaTileComponent
                      key={mediaItem.fullFileUrl}
                      media={mediaItem}
                      onDelete={onDeleteMedia}
                      onShowPresets={onShowPresets}
                    />
                  ))}
                </Box>
              </Box>
            );
          },
        )}

        {/* Render media with unknown customer */}
        {groupedMedia.unknownCustomerMedia.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: 'text.secondary',
              }}
            >
              Unknown Customer
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                justifyContent: 'flex-start',
                alignItems: 'flex-start',
              }}
            >
              {groupedMedia.unknownCustomerMedia.map((mediaItem) => (
                <MediaTileComponent
                  key={mediaItem.fullFileUrl}
                  media={mediaItem}
                  onDelete={onDeleteMedia}
                  onShowPresets={onShowPresets}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Show message when no media */}
        {media.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              textAlign: 'center',
              width: '100%',
              py: 4,
            }}
          >
            Drag media here to remove from folders
          </Typography>
        )}
      </DropZone>
    </Box>
  );
}

export default OtherMediaSection;
